import { KeyboardEvent } from 'react';

const PIPE_ADDON_WIDTH = 20;

export interface ChipInfo {
  index: number;
  rect: DOMRect;
}

export interface VerticalNavigationParams {
  event: KeyboardEvent<HTMLInputElement | HTMLTextAreaElement>;
  index: number;
  editingValue: string;
  chipRefs: Map<number, HTMLElement>;
  onCommit: (index: number, value: string) => void;
  onNavigate: (
    targetIndex: number,
    targetValue: string,
    cursorPosition: number,
  ) => void;
  refinements: string[];
  containerRect: DOMRect;
}

/**
 * Checks if the cursor should navigate vertically between chips
 * Returns true if navigation should occur, false if textarea should handle it
 */
export function shouldNavigateVertically(
  event: KeyboardEvent<HTMLInputElement | HTMLTextAreaElement>,
  editingValue: string,
  chipRect: DOMRect,
): boolean {
  const textarea = event.currentTarget as HTMLTextAreaElement;
  const currentCursorPos = textarea.selectionStart || 0;

  // Create a temporary element to measure text layout
  const tempDiv = document.createElement('div');
  tempDiv.style.position = 'absolute';
  tempDiv.style.visibility = 'hidden';
  tempDiv.style.whiteSpace = 'pre-wrap';
  tempDiv.style.wordWrap = 'break-word';
  tempDiv.style.fontSize = '0.875rem';
  tempDiv.style.lineHeight = '1.25rem';
  tempDiv.style.fontFamily = window.getComputedStyle(textarea).fontFamily;
  tempDiv.style.width = `${chipRect.width - 16 - PIPE_ADDON_WIDTH}px`; // Match textarea width

  // Create a single text node up to cursor position, then a marker span
  if (currentCursorPos > 0) {
    tempDiv.appendChild(
      document.createTextNode(editingValue.slice(0, currentCursorPos)),
    );
  }

  const cursorMarker = document.createElement('span');
  cursorMarker.id = 'cursor-position';
  cursorMarker.style.display = 'inline-block';
  cursorMarker.style.width = '1px';
  cursorMarker.style.height = '1.25rem'; // Match line height
  tempDiv.appendChild(cursorMarker);

  if (currentCursorPos < editingValue.length) {
    tempDiv.appendChild(
      document.createTextNode(editingValue.slice(currentCursorPos)),
    );
  }

  document.body.appendChild(tempDiv);

  const cursorSpan = tempDiv.querySelector('#cursor-position') as HTMLElement;
  if (!cursorSpan) {
    document.body.removeChild(tempDiv);
    return true; // Default to allowing navigation
  }

  const cursorRect = cursorSpan.getBoundingClientRect();
  const containerRect = tempDiv.getBoundingClientRect();

  // Calculate if we're on the first or last line
  const lineHeight = 20; // 1.25rem = 20px
  const containerHeight = containerRect.height;
  const isSingleLine = containerHeight <= lineHeight * 1.5; // Allow some margin

  if (isSingleLine) {
    // For single-line chips, always allow navigation
    document.body.removeChild(tempDiv);
    return true;
  } else {
    // For multi-line chips, check if we're on first/last line
    const isOnFirstLine = cursorRect.top <= containerRect.top + 5; // Small margin for precision
    const isOnLastLine = cursorRect.bottom >= containerRect.bottom - 5;

    document.body.removeChild(tempDiv);

    // Only navigate to different chip if we're at the edge
    if (
      (event.key === 'ArrowUp' && !isOnFirstLine) ||
      (event.key === 'ArrowDown' && !isOnLastLine)
    ) {
      return false; // Let default textarea behavior handle it
    }
  }

  return true;
}

/**
 * Calculates the cursor X position within the current chip
 */
export function calculateCursorXPosition(
  textarea: HTMLTextAreaElement,
  editingValue: string,
  currentCursorPos: number,
  chipRect: DOMRect,
): number {
  // Get cursor X position within the chip
  const measureDiv = document.createElement('div');
  measureDiv.style.position = 'absolute';
  measureDiv.style.visibility = 'hidden';
  measureDiv.style.whiteSpace = 'pre-wrap';
  measureDiv.style.wordWrap = 'break-word';
  measureDiv.style.fontSize = '0.875rem';
  measureDiv.style.lineHeight = '1.25rem';
  measureDiv.style.fontFamily = window.getComputedStyle(textarea).fontFamily;
  measureDiv.style.width = `${chipRect.width - 16 - PIPE_ADDON_WIDTH}px`; // Match textarea width

  // Add text up to cursor position
  if (currentCursorPos > 0) {
    measureDiv.appendChild(
      document.createTextNode(editingValue.slice(0, currentCursorPos)),
    );
  }

  // Add a marker at cursor position
  const marker = document.createElement('span');
  marker.id = 'cursor-x-marker';
  marker.textContent = '|';
  measureDiv.appendChild(marker);

  document.body.appendChild(measureDiv);

  // Get the marker position relative to the container
  const markerRect = marker.getBoundingClientRect();
  const containerLeft = measureDiv.getBoundingClientRect().left;
  const relativeX = markerRect.left - containerLeft;

  document.body.removeChild(measureDiv);

  // Calculate absolute cursor X position
  // The text starts at chip.left + PIPE_ADDON_WIDTH + 8px padding
  const cursorX = chipRect.left + PIPE_ADDON_WIDTH + 8 + relativeX;

  return cursorX;
}

/**
 * Finds the target chip for vertical navigation
 */
export function findTargetChip(
  direction: 'ArrowUp' | 'ArrowDown',
  currentChipRect: DOMRect,
  cursorX: number,
  chipRefs: Map<number, HTMLElement>,
): ChipInfo | null {
  // Find all chips and their positions
  const chips = Array.from(chipRefs.entries())
    .map(([idx, el]) => ({
      index: idx,
      rect: el.getBoundingClientRect(),
    }))
    .sort((a, b) => {
      // Sort by Y position first, then X position
      if (Math.abs(a.rect.top - b.rect.top) > 5) {
        return a.rect.top - b.rect.top;
      }
      return a.rect.left - b.rect.left;
    });

  let targetChip = null;

  if (direction === 'ArrowUp') {
    // Find chips in the row above
    const currentY = currentChipRect.top;
    const chipsAbove = chips.filter((c) => c.rect.bottom < currentY - 5);

    if (chipsAbove.length > 0) {
      // Find the chip with the closest X position in the row above
      const lastRowY = chipsAbove[chipsAbove.length - 1].rect.top;
      const chipsInTargetRow = chipsAbove.filter(
        (c) => Math.abs(c.rect.top - lastRowY) < 5,
      );

      targetChip = chipsInTargetRow.reduce((closest, chip) => {
        const closestDist = Math.abs(
          closest.rect.left + closest.rect.width / 2 - cursorX,
        );
        const chipDist = Math.abs(
          chip.rect.left + chip.rect.width / 2 - cursorX,
        );
        return chipDist < closestDist ? chip : closest;
      });
    }
  } else {
    // ArrowDown - find chips in the row below
    const currentY = currentChipRect.bottom;
    const chipsBelow = chips.filter((c) => c.rect.top > currentY + 5);

    if (chipsBelow.length > 0) {
      // Find the chip with the closest X position in the row below
      const firstRowY = chipsBelow[0].rect.top;
      const chipsInTargetRow = chipsBelow.filter(
        (c) => Math.abs(c.rect.top - firstRowY) < 5,
      );

      targetChip = chipsInTargetRow.reduce((closest, chip) => {
        const closestDist = Math.abs(
          closest.rect.left + closest.rect.width / 2 - cursorX,
        );
        const chipDist = Math.abs(
          chip.rect.left + chip.rect.width / 2 - cursorX,
        );
        return chipDist < closestDist ? chip : closest;
      });
    }
  }

  return targetChip;
}

export function getLastLineOfText(spanElement: HTMLElement): string {
  const originalText = spanElement.textContent || '';
  const originalHeight = spanElement.clientHeight;

  if (originalHeight === 0) {
    return ''; // Span is not visible or has no height
  }

  // Create a temporary, hidden span to perform measurements
  const tempSpan = document.createElement('span');
  tempSpan.style.visibility = 'hidden';
  tempSpan.style.position = 'absolute'; // Position absolute to not affect layout
  tempSpan.style.left = '-9999px'; // Move it far off-screen
  tempSpan.style.top = '-9999px';
  tempSpan.style.width = spanElement.offsetWidth + 'px'; // Match the original span's width
  tempSpan.style.fontSize = getComputedStyle(spanElement).fontSize;
  tempSpan.style.fontFamily = getComputedStyle(spanElement).fontFamily;
  tempSpan.style.lineHeight = getComputedStyle(spanElement).lineHeight;
  tempSpan.style.whiteSpace = 'normal'; // Ensure text wraps normally
  document.body.appendChild(tempSpan);

  let lastLineText = '';

  // Binary search approach to find the cut-off point
  let low = 0;
  let high = originalText.length;
  let bestCutoff = 0;

  while (low <= high) {
    const mid = Math.floor((low + high) / 2);
    const testText = originalText.substring(0, mid);
    tempSpan.textContent = testText;
    if (tempSpan.clientHeight < originalHeight) {
      // This substring is too short, it makes the total text shorter than the original height.
      // This means the last line starts after 'mid'.
      bestCutoff = mid;
      low = mid + 1;
    } else {
      // This substring is still tall enough, or too tall.
      // This means the last line starts at or before 'mid'.
      high = mid - 1;
    }
  }

  // Now, bestCutoff is the approximate index where the text *before* the last line ends.
  // We need to find the first character of the last line.
  let lastLineStartIndex = bestCutoff;
  while (lastLineStartIndex < originalText.length) {
    tempSpan.textContent = originalText.substring(0, lastLineStartIndex);
    if (tempSpan.clientHeight < originalHeight) {
      // This means lastLineStartIndex is still within the previous lines.
      // We need to keep going forward to find the actual start of the last line.
      lastLineStartIndex++;
    } else {
      // We've found the point where adding this character causes the text to wrap to the next line.
      // So, this character and subsequent ones are on the last line.
      break;
    }
  }

  // Now, let's refine by checking if the very first character found actually belongs to the last line.
  // We iterate forward from 'bestCutoff' to find the actual start of the last line.
  for (let i = bestCutoff; i < originalText.length; i++) {
    tempSpan.textContent = originalText.substring(0, i);
    if (tempSpan.clientHeight < originalHeight) {
      lastLineStartIndex = i;
    } else {
      break;
    }
  }

  lastLineText = originalText.substring(lastLineStartIndex);

  // Clean up the temporary span
  document.body.removeChild(tempSpan);

  return lastLineText.trim(); // Trim to remove any leading/trailing whitespace from the substring
}

/**
 * Calculates the cursor position in the target chip based on X coordinate
 */
export function calculateTargetCursorPosition(
  direction: 'ArrowUp' | 'ArrowDown',
  targetValue: string,
  cursorX: number,
  targetChipRect: DOMRect,
  textarea: HTMLTextAreaElement,
  containerRect: DOMRect,
): number {
  let targetCursorPos = 0;
  if (targetValue && cursorX > targetChipRect.left) {
    const tempSpan = document.createElement('span');
    tempSpan.style.visibility = 'hidden';
    tempSpan.style.position = 'absolute';
    tempSpan.style.fontSize = '0.875rem';
    tempSpan.style.fontFamily = window.getComputedStyle(textarea).fontFamily;
    tempSpan.style.whiteSpace = 'pre';
    document.body.appendChild(tempSpan);

    let extra = 0;

    // When moving up we need to take into account multiple lines of text since the cursor
    // should move into location in the last line of text. Do this we need to calculate what
    // the last of of text is and then subtract that from the total length of the text.
    if (direction === 'ArrowUp') {
      // Create a new span with the specified styles
      const lastLineSpan = document.createElement('span');
      lastLineSpan.style.fontSize = '0.875rem';
      lastLineSpan.style.lineHeight = '1.25rem';
      lastLineSpan.style.fontFamily = 'inherit';
      lastLineSpan.style.whiteSpace = 'pre-wrap';
      lastLineSpan.style.overflowWrap = 'break-word';
      lastLineSpan.style.boxSizing = 'border-box';
      lastLineSpan.style.visibility = 'hidden';
      lastLineSpan.style.position = 'absolute';
      lastLineSpan.style.width = `${containerRect.width - 20 - PIPE_ADDON_WIDTH}px`;
      lastLineSpan.textContent = targetValue;
      document.body.appendChild(lastLineSpan);
      const lastLine = getLastLineOfText(lastLineSpan);
      extra = targetValue.length - lastLine.length;
      document.body.removeChild(lastLineSpan);
    }

    // Binary search to find the closest character position
    let left = 0;
    let right = targetValue.length;
    const relativeX = cursorX - targetChipRect.left - PIPE_ADDON_WIDTH - 8; // Subtract padding and pipe addon

    while (left < right) {
      const mid = Math.floor((left + right) / 2);
      tempSpan.textContent = targetValue.slice(0, mid);
      const width = tempSpan.getBoundingClientRect().width;

      if (width < relativeX) {
        left = mid + 1;
      } else {
        right = mid;
      }
    }

    targetCursorPos = left;
    targetCursorPos += extra;
    document.body.removeChild(tempSpan);
  }

  return targetCursorPos;
}

/**
 * Handles vertical navigation between chips
 */
export function handleVerticalNavigation(
  params: VerticalNavigationParams,
): boolean {
  const {
    event,
    index,
    editingValue,
    chipRefs,
    onCommit,
    onNavigate,
    refinements,
    containerRect,
  } = params;

  const textarea = event.currentTarget as HTMLTextAreaElement;
  const currentCursorPos = textarea.selectionStart || 0;

  const currentChip = chipRefs.get(index);
  if (!currentChip) return false;

  const chipRect = currentChip.getBoundingClientRect();

  // Check if we should navigate or let textarea handle it
  if (!shouldNavigateVertically(event, editingValue, chipRect)) {
    return false; // Let default textarea behavior handle it
  }

  event.preventDefault();

  // Calculate cursor X position
  const cursorX = calculateCursorXPosition(
    textarea,
    editingValue,
    currentCursorPos,
    chipRect,
  );

  // Find target chip
  const targetChip = findTargetChip(
    event.key as 'ArrowUp' | 'ArrowDown',
    chipRect,
    cursorX,
    chipRefs,
  );

  if (targetChip) {
    onCommit(index, editingValue);
    const targetValue = refinements[targetChip.index] || '';

    // Calculate cursor position in target chip
    const targetCursorPos = calculateTargetCursorPosition(
      event.key as 'ArrowUp' | 'ArrowDown',
      targetValue,
      cursorX,
      targetChip.rect,
      textarea,
      containerRect,
    );

    onNavigate(targetChip.index, targetValue, targetCursorPos);
  }

  return true;
}
